/**
 * The attribute scope applied to the Reports view the current user is looking at
 * (used by the header chip cluster, the scope-locked slice picker, and the
 * Report Insights subtitle).
 *
 * `useFetchCurrentUserAttributeScope(baseDivisionId)` fetches
 * `/api/roles/current_user/?base_division_id=…` and derives the active role's
 * scope — keyed by division so it refetches as the user switches divisions in
 * the report dropdown. `ReportsPage` calls it once and publishes the result
 * through `AppliedAttributeScopeContext`; the nested consumers read it with the
 * `useCurrentUserAttributeScope` reader below, so they need no division context.
 */
import { createContext, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RoleManager } from '../../../APIManager'
import { getCurrentRole } from '../../../helpers/roleHelpers'
import { type UserRole } from '../../../models/UserRoles'
import {
  type AppliedAttributeScope,
  deriveAppliedAttributeScope
} from '../helpers/deriveAppliedAttributeScope'

const EMPTY_SCOPE: AppliedAttributeScope = {
  courseAttributes: [],
  facultyAttributes: []
}

interface AttributeScopeReturn {
  appliedAttributeScope: AppliedAttributeScope
}

export const useFetchCurrentUserAttributeScope = (
  baseDivisionId: string | null | undefined
): AttributeScopeReturn => {
  const { data } = useQuery<UserRole[]>({
    queryKey: ['currentUserRoles', baseDivisionId ?? null],
    queryFn: async () => {
      const response = await RoleManager.getCurrentUserRole(baseDivisionId ?? undefined)
      return response.data
    },
    enabled: baseDivisionId != null,
    staleTime: 5 * 60 * 1000 // role scope rarely changes within a session for a given division
  })

  const appliedAttributeScope = useMemo(
    () => deriveAppliedAttributeScope(data != null ? getCurrentRole(data) : undefined),
    [data]
  )

  return { appliedAttributeScope }
}

export const AppliedAttributeScopeContext = createContext<AppliedAttributeScope>(EMPTY_SCOPE)

export const useCurrentUserAttributeScope = (): AttributeScopeReturn => {
  const appliedAttributeScope = useContext(AppliedAttributeScopeContext)
  return { appliedAttributeScope }
}
